import axios from 'axios';

import dpiasApi from 'config/api/dpias/dpias';
import type { DpiaDetail, DpiaRisk } from 'config/api/dpias/dpias.types';

import { mapFormValuesToUpdateInput, type OwnerDpiaFormValues } from './ownerDpiaEditor.helpers';

const isTemporaryId = (id: number) => id < 0;

const applyRevisionFromResponse = (currentRevision: number, revision: number | null): number =>
  revision == null ? currentRevision : revision;

type SyncNestedRisksAndMeasuresResult = {
  revision: number;
  hadNestedMutation: boolean;
};

const syncNestedRisksAndMeasures = async (
  organizationId: number,
  dpiaId: number,
  oldRisks: DpiaRisk[],
  newRisks: DpiaRisk[],
  currentRevision: number,
  progress: (revision: number, path: string) => void,
): Promise<SyncNestedRisksAndMeasuresResult> => {
  let revision = currentRevision;
  let hadNestedMutation = false;

  const trackRevision = (responseRevision: number | null) => {
    hadNestedMutation = true;
    revision = applyRevisionFromResponse(revision, responseRevision);
  };

  const risksToDelete = oldRisks.filter(oldRisk => !newRisks.some(newRisk => newRisk.id === oldRisk.id));
  const deletedRiskIds = new Set(risksToDelete.map(risk => risk.id));
  if (risksToDelete.length > 0) {
    hadNestedMutation = true;
    await Promise.all(risksToDelete.map(risk => dpiasApi.deleteRisk(organizationId, dpiaId, risk.id)));
  }

  const knownRiskIds = new Set(oldRisks.map(risk => risk.id).filter(riskId => !isTemporaryId(riskId) && !deletedRiskIds.has(riskId)));

  // Sequential creates so temporary measure ids can reference the new risk id.
  // eslint-disable-next-line no-restricted-syntax
  for (const newRisk of newRisks) {
    const riskPath = `risk.${newRisks.indexOf(newRisk)}`;
    progress(revision, riskPath);
    const isNewRisk = isTemporaryId(newRisk.id);
    let realRiskId = newRisk.id;

    if (isNewRisk) {
      // eslint-disable-next-line no-await-in-loop
      const createdRiskResponse = await dpiasApi.createRisk(
        organizationId,
        dpiaId,
        {
          title: newRisk.title,
          description: newRisk.description,
          affectedRightsAndFreedoms: newRisk.affectedRightsAndFreedoms,
          likelihood: newRisk.likelihood,
          impact: newRisk.impact,
          sortOrder: newRisk.sortOrder,
        },
        { knownRiskIds: Array.from(knownRiskIds) },
      );
      trackRevision(createdRiskResponse.revision);
      realRiskId = createdRiskResponse.data.id;
      knownRiskIds.add(realRiskId);
      newRisk.id = realRiskId;
      newRisk.measures = newRisk.measures.map(measure => ({ ...measure, dpiaRiskId: realRiskId }));
    } else {
      const oldRisk = oldRisks.find(risk => risk.id === newRisk.id);
      const needsUpdate =
        !oldRisk ||
        oldRisk.title !== newRisk.title ||
        oldRisk.description !== newRisk.description ||
        oldRisk.affectedRightsAndFreedoms !== newRisk.affectedRightsAndFreedoms ||
        oldRisk.likelihood !== newRisk.likelihood ||
        oldRisk.impact !== newRisk.impact ||
        oldRisk.sortOrder !== newRisk.sortOrder;

      if (needsUpdate) {
        // eslint-disable-next-line no-await-in-loop
        const updatedRiskResponse = await dpiasApi.updateRisk(organizationId, dpiaId, realRiskId, {
          title: newRisk.title,
          description: newRisk.description,
          affectedRightsAndFreedoms: newRisk.affectedRightsAndFreedoms,
          likelihood: newRisk.likelihood,
          impact: newRisk.impact,
          sortOrder: newRisk.sortOrder,
        });
        trackRevision(updatedRiskResponse.revision);
      }
    }

    const oldMeasures = isNewRisk ? [] : oldRisks.find(risk => risk.id === newRisk.id)?.measures || [];
    const newMeasures = newRisk.measures;

    const measuresToDelete = oldMeasures.filter(oldMeasure => !newMeasures.some(newMeasure => newMeasure.id === oldMeasure.id));
    if (measuresToDelete.length > 0) {
      hadNestedMutation = true;
      // eslint-disable-next-line no-await-in-loop
      await Promise.all(measuresToDelete.map(measure => dpiasApi.deleteMeasure(organizationId, dpiaId, measure.id)));
    }

    // eslint-disable-next-line no-restricted-syntax
    for (const newMeasure of newMeasures) {
      progress(revision, `${riskPath}.measure.${newMeasures.indexOf(newMeasure)}`);
      if (isTemporaryId(newMeasure.id)) {
        // eslint-disable-next-line no-await-in-loop
        const createdMeasureResponse = await dpiasApi.createMeasure(organizationId, dpiaId, {
          dpiaRiskId: realRiskId,
          title: newMeasure.title,
          description: newMeasure.description,
          responsibleMembershipId: newMeasure.responsibleMembershipId,
          dueDate: newMeasure.dueDate,
          status: newMeasure.status,
          sortOrder: newMeasure.sortOrder,
        });
        newMeasure.id = createdMeasureResponse.data.id;
        trackRevision(createdMeasureResponse.revision);
      } else {
        const oldMeasure = oldMeasures.find(measure => measure.id === newMeasure.id);
        const needsUpdate =
          !oldMeasure ||
          oldMeasure.title !== newMeasure.title ||
          oldMeasure.description !== newMeasure.description ||
          oldMeasure.responsibleMembershipId !== newMeasure.responsibleMembershipId ||
          oldMeasure.dueDate !== newMeasure.dueDate ||
          oldMeasure.status !== newMeasure.status ||
          oldMeasure.sortOrder !== newMeasure.sortOrder;

        if (needsUpdate) {
          // eslint-disable-next-line no-await-in-loop
          const updatedMeasureResponse = await dpiasApi.updateMeasure(organizationId, dpiaId, newMeasure.id, {
            title: newMeasure.title,
            description: newMeasure.description,
            responsibleMembershipId: newMeasure.responsibleMembershipId,
            dueDate: newMeasure.dueDate,
            status: newMeasure.status,
            sortOrder: newMeasure.sortOrder,
          });
          trackRevision(updatedMeasureResponse.revision);
        }
      }
    }
  }

  if (hadNestedMutation && revision === currentRevision) {
    const {
      data: {
        dpia: { revision: refreshedRevision },
      },
    } = await dpiasApi.getDetail(organizationId, dpiaId);
    revision = refreshedRevision;
  }

  return { revision, hadNestedMutation };
};

export class DpiaDraftSaveError extends Error {
  originalError: unknown;

  values: OwnerDpiaFormValues;

  originalValues: OwnerDpiaFormValues;

  fields: Partial<Record<string, string>>;

  constructor(
    originalError: unknown,
    values: OwnerDpiaFormValues,
    originalValues: OwnerDpiaFormValues,
    fields: Partial<Record<string, string>>,
  ) {
    super('DPIA draft save failed');
    this.originalError = originalError;
    this.values = values;
    this.originalValues = originalValues;
    this.fields = fields;
  }
}
export const persistDpiaDraft = async (
  organizationId: number,
  dpiaId: number,
  currentDetail: DpiaDetail,
  formValues: OwnerDpiaFormValues,
) => {
  const workingValues = {
    ...formValues,
    risks: formValues.risks.map(risk => ({ ...risk, measures: risk.measures.map(measure => ({ ...measure })) })),
  };
  let path = '';
  try {
    const { revision, hadNestedMutation } = await syncNestedRisksAndMeasures(
      organizationId,
      dpiaId,
      currentDetail.risks,
      workingValues.risks,
      currentDetail.revision,
      (_revision, nextPath) => {
        if (nextPath !== undefined) path = nextPath;
      },
    );

    path = '';
    const { data: patchData } = await dpiasApi.update(organizationId, dpiaId, mapFormValuesToUpdateInput(revision, workingValues));

    if (!hadNestedMutation) {
      return patchData;
    }

    const { data } = await dpiasApi.getDetail(organizationId, dpiaId);
    return data;
  } catch (cause) {
    const fields: Partial<Record<string, string>> = {};
    if (axios.isAxiosError(cause) && cause.response?.status === 400) {
      const payload = cause.response.data;
      if (payload && typeof payload === 'object') {
        Object.entries(payload).forEach(([key, value]) => {
          const field = key === 'responsible_membership_id' ? 'responsible' : key.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
          fields[path ? `${path}.${field}` : field] = Array.isArray(value) ? value.join(' ') : String(value);
        });
      }
    }
    throw new DpiaDraftSaveError(cause, workingValues, formValues, fields);
  }
};

export const reconcileDraftIds = (current: OwnerDpiaFormValues, failure: DpiaDraftSaveError): OwnerDpiaFormValues => ({
  ...current,
  risks: current.risks.map(risk => {
    const index = failure.originalValues.risks.findIndex(original => original.id === risk.id);
    if (index < 0) return risk;
    const saved = failure.values.risks[index];
    const original = failure.originalValues.risks[index];
    return {
      ...risk,
      id: saved.id,
      measures: risk.measures.map(measure => {
        const measureIndex = original.measures.findIndex(item => item.id === measure.id);
        return measureIndex < 0 ? measure : { ...measure, id: saved.measures[measureIndex].id, dpiaRiskId: saved.id };
      }),
    };
  }),
});
